import { useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/app/lib/cn'

/** How long a press has to be held before it counts as "look at this one". */
const LONG_PRESS_MS = 450
/** Past this much movement it is a scroll, not a press. */
const LONG_PRESS_SLOP = 12
/** Two taps closer together than this are one gesture. */
const DOUBLE_CLICK_MS = 320

/**
 * A selectable photo.
 *
 * Selection is shown three ways at once — a ring, a tint, and a numbered badge
 * — because exactly one of them is reliable in every situation: colour alone
 * fails in a dim venue at an angle, and a ring alone is easy to miss on a busy
 * contact sheet. The badge also carries the *order* of selection, which is what
 * decides which slot each photo lands in when it is composited.
 *
 * Three ways in beyond a tap, because an operator checking a shot before
 * committing to it is the common case: double click, long press on a touch
 * monitor, or right click for the menu.
 */

export function PhotoTile({
  src,
  alt,
  selected,
  order,
  onToggle,
  onPreview,
  onContextMenu,
}: {
  /** Undefined until the thumbnail has been rendered locally. */
  src?: string
  alt: string
  selected: boolean
  /** 1-based position in the selection, which maps to the layout slot. */
  order?: number
  onToggle: () => void
  /** Double click, or a long press on a touch monitor. */
  onPreview?: () => void
  onContextMenu?: (event: React.MouseEvent) => void
}) {
  const [loaded, setLoaded] = useState(false)

  /**
   * Long-press state.
   *
   * `fired` is what stops the press from also toggling selection: the pointer
   * sequence still ends in a click, and an operator who held a tile to look at
   * it did not mean to pick it as well.
   */
  const press = useRef<{ timer?: ReturnType<typeof setTimeout>; fired: boolean; x: number; y: number }>({
    fired: false,
    x: 0,
    y: 0,
  })

  const cancelPress = () => {
    if (press.current.timer) clearTimeout(press.current.timer)
    press.current.timer = undefined
  }

  const startPress = (event: React.PointerEvent) => {
    if (!onPreview) return
    press.current = { fired: false, x: event.clientX, y: event.clientY }
    press.current.timer = setTimeout(() => {
      press.current.fired = true
      onPreview()
    }, LONG_PRESS_MS)
  }

  const movePress = (event: React.PointerEvent) => {
    if (!press.current.timer) return
    const moved =
      Math.abs(event.clientX - press.current.x) + Math.abs(event.clientY - press.current.y)
    if (moved > LONG_PRESS_SLOP) cancelPress()
  }

  /**
   * Timestamp of the previous click, for the double-tap check.
   *
   * Timed here rather than left to the DOM's own `dblclick`, because that event
   * is a mouse event: a double *tap* on the touch monitor does not reliably
   * produce one, and the operator using a touchscreen is the one most likely to
   * want a closer look before committing.
   */
  const lastClick = useRef(0)

  return (
    <button
      type="button"
      onClick={() => {
        // A long press already did its job; do not also select.
        if (press.current.fired) {
          press.current.fired = false
          return
        }

        // Toggle on every click, including the second of a pair. Selecting has
        // to feel immediate — waiting out the double-click interval before a
        // tap does anything would make the whole gallery feel broken — and the
        // second toggle undoes the first, so a double click lands the operator
        // back where they started with the preview open.
        onToggle()

        const now = Date.now()
        if (onPreview && now - lastClick.current < DOUBLE_CLICK_MS) {
          lastClick.current = 0
          onPreview()
        } else {
          lastClick.current = now
        }
      }}
      onContextMenu={onContextMenu}
      onPointerDown={startPress}
      onPointerMove={movePress}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      aria-pressed={selected}
      aria-label={selected ? `${alt} — dipilih (${order})` : alt}
      className={cn(
        // NOT `touch-none`: the tiles cover most of the gallery, so suppressing
        // touch panning on them makes a touch monitor unable to scroll it. The
        // long-press slop check is what tells a press from a scroll.
        'group relative aspect-square select-none overflow-hidden rounded-[14px]',
        'bg-raised ring-1 transition-[box-shadow,transform] duration-100',
        'active:scale-[.98]',
        selected ? 'ring-2 ring-brand' : 'ring-line',
      )}
    >
      {/*
        `contain`, not `cover`. A square crop would hide whether the photo is
        portrait or landscape — and orientation is exactly what decides which
        slot it can go in, so a photo cropped to look square can be silently
        dropped from the composite with no warning the operator could have seen.

        `lazy` matters more than it looks: an event folder holds several hundred
        frames and the grid renders a tile for each, but only what the operator
        can actually see is ever fetched.
      */}
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          // An image the browser already holds decoded is `complete` the moment
          // it is attached, and `load` may have fired before React listened.
          // Without this a remounted grid faded every tile in from blank —
          // 200ms of empty squares for thumbnails that were already on screen
          // a moment ago.
          ref={(img) => {
            if (img && !loaded && img.complete && img.naturalWidth > 0) setLoaded(true)
          }}
          onLoad={() => setLoaded(true)}
          className={cn(
            'h-full w-full object-contain transition-opacity duration-200',
            // Fade in rather than pop: on a folder of several hundred frames the
            // tiles arrive over several seconds, and flashing rectangles read as
            // the app struggling.
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}

      {selected && (
        <>
          <span aria-hidden className="absolute inset-0 bg-brand/20" />
          <span
            aria-hidden
            className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-brand text-on-brand shadow-lg"
          >
            {order ? (
              <span className="text-[15px] font-bold">{order}</span>
            ) : (
              <Check size={18} strokeWidth={3} />
            )}
          </span>
        </>
      )}
    </button>
  )
}
